import { usePublish } from "@booking/hooks/usePubSub";
import clsx from "clsx";
import React from "react";

import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";
import { gaClickAttrs } from "@/utils/tracking";

import styles from "./DropdownInput.module.css";

const ref = "dropdown-input";

enum DropdownInputEventEnum {
  SELECTED_INDEX_CHANGE = "selected_index_change",
}

interface OptionValue extends Partial<HTMLOptionElement> {}

interface DropdownInputProps extends Partial<CommonProps> {
  fullWidth?: boolean;
  hideLabel?: boolean;
  label: string;
  name: string;
  noWrap?: boolean;
  options: Array<OptionValue>;
  readonly?: boolean;
  selectedValue: string;
}

function useDropDownInput(props: DropdownInputProps) {
  const { color, selectedValue, id = createId(ref), options = [] } = props;
  const className = clsx(
    styles[ref],
    color && styles[color],
    props.fullWidth && styles["full-width"],
    props.hideLabel && styles["no-label"],
    props.noWrap && styles["no-wrap"],
    props.readonly && styles.readonly,
    props.className,
  );

  return {
    className,
    id,
    options,
    selectedValue,
  };
}

function DropdownInput(props: DropdownInputProps) {
  const { eventGroup } = props;
  const { className, id, selectedValue, options } = useDropDownInput(props);
  const publish = usePublish({ context: props.name, ...props.pubSubData });

  function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    publish(DropdownInputEventEnum.SELECTED_INDEX_CHANGE, {
      value: e.target.value,
    });
  }

  return (
    <label className={className} htmlFor={id}>
      <span>{props.label}</span>

      <select
        id={id}
        name={props.name}
        tabIndex={props.readonly ? -1 : undefined}
        value={selectedValue}
        onChange={onChange}
        {...gaClickAttrs({ eventGroup, click: `${props.name}_click` })}
      >
        {options.map((option, index) => (
          <option key={`${id}_${index}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default DropdownInput;
export { DropdownInput, DropdownInputEventEnum };
export type { DropdownInputProps, OptionValue };
